/**
 * SDK Reference Detail Page — /sdk/:slug
 *
 * Renders a single SDK entry (method, type, or guide) with
 * signature, parameters, return type, examples, pitfalls, and see-also links.
 * Inspired by react.dev individual reference pages.
 */

import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import {
  getEntry,
  SDK_CATEGORIES,
  type CodeExample,
} from '../../data/sdk-docs';

export default function SdkReferencePage() {
  const { slug } = useParams<{ slug: string }>();
  const entry = slug ? getEntry(slug) : undefined;

  if (!entry) {
    return <Navigate to="/sdk" replace />;
  }

  const { prev, next } = getAdjacentEntries(entry.slug);

  return (
    <article className="sdk-ref">
      {/* Breadcrumb */}
      <nav className="sdk-breadcrumb">
        <Link to="/sdk">SDK Reference</Link>
        <span className="sdk-breadcrumb-sep">/</span>
        <span>{entry.category}</span>
        <span className="sdk-breadcrumb-sep">/</span>
        <span className="sdk-breadcrumb-current">{entry.title}</span>
      </nav>

      {/* Page heading */}
      <header className="sdk-ref-header">
        <div className="sdk-ref-badges">
          <span className={`sdk-kind-badge ${entry.kind}`}>
            {kindLabel(entry.kind)}
          </span>
          {entry.since && <span className="sdk-since">Added in v{entry.since}</span>}
        </div>
        <h1>{entry.title}</h1>
        <p className="sdk-ref-summary">{entry.summary}</p>
      </header>

      {/* Signature */}
      {entry.signature && (
        <section className="sdk-section">
          <h2>Signature</h2>
          <CodeBlock code={entry.signature} />
        </section>
      )}

      {/* Parameters */}
      {entry.params && entry.params.length > 0 && (
        <section className="sdk-section">
          <h2>{entry.kind === 'type' ? 'Properties' : 'Parameters'}</h2>
          <div className="sdk-params-table-wrap">
            <table className="sdk-params-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {entry.params.map((p) => (
                  <tr key={p.name}>
                    <td>
                      <code>{p.name}</code>
                    </td>
                    <td>
                      <code className="sdk-type-ref">{p.type}</code>
                    </td>
                    <td>
                      {p.required ? (
                        <span className="sdk-required">Required</span>
                      ) : (
                        <span className="sdk-optional">
                          Optional{p.default ? ` (${p.default})` : ''}
                        </span>
                      )}
                    </td>
                    <td>{p.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Returns */}
      {entry.returns && (
        <section className="sdk-section">
          <h2>Returns</h2>
          <div className="sdk-returns">
            <code className="sdk-type-ref">{entry.returns.type}</code>
            <span> — {entry.returns.description}</span>
          </div>
        </section>
      )}

      {/* Description */}
      <section className="sdk-section">
        <h2>Description</h2>
        <p className="sdk-description">{entry.description}</p>
      </section>

      {/* Examples */}
      {entry.examples.length > 0 && (
        <section className="sdk-section">
          <h2>Examples</h2>
          {entry.examples.map((ex, i) => (
            <ExampleBlock key={i} example={ex} />
          ))}
        </section>
      )}

      {/* Pitfalls */}
      {entry.pitfalls && entry.pitfalls.length > 0 && (
        <section className="sdk-section">
          <h2>
            <AlertTriangle size={18} className="sdk-pitfall-icon" />
            Pitfalls
          </h2>
          <ul className="sdk-pitfalls">
            {entry.pitfalls.map((pit, i) => (
              <li key={i}>{pit}</li>
            ))}
          </ul>
        </section>
      )}

      {/* See Also */}
      {entry.seeAlso && entry.seeAlso.length > 0 && (
        <section className="sdk-section">
          <h2>See Also</h2>
          <div className="sdk-see-also">
            {entry.seeAlso.map((slug) => {
              const related = getEntry(slug);
              if (!related) return null;
              return (
                <Link
                  key={slug}
                  to={`/sdk/${slug}`}
                  className="sdk-see-also-link"
                >
                  {related.title}
                  <ExternalLink size={12} />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Prev / Next */}
      <nav className="sdk-pagination">
        {prev ? (
          <Link to={`/sdk/${prev.slug}`} className="sdk-pagination-link prev">
            <ChevronLeft size={16} />
            <div>
              <span className="sdk-pagination-label">Previous</span>
              <span className="sdk-pagination-title">{prev.title}</span>
            </div>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`/sdk/${next.slug}`} className="sdk-pagination-link next">
            <div>
              <span className="sdk-pagination-label">Next</span>
              <span className="sdk-pagination-title">{next.title}</span>
            </div>
            <ChevronRight size={16} />
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </article>
  );
}

// ── Sub-components ────────────────────────────────────

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="sdk-code-block">
      <button className="sdk-copy-btn" onClick={copy} title="Copy">
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function ExampleBlock({ example }: { example: CodeExample }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(example.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="sdk-example">
      <div className="sdk-example-header">
        <h3>{example.title}</h3>
        <button className="sdk-copy-btn" onClick={copy} title="Copy">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      {example.description && (
        <p className="sdk-example-desc">{example.description}</p>
      )}
      <pre className="sdk-example-code">
        <code>{example.code}</code>
      </pre>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────

function kindLabel(kind: string): string {
  switch (kind) {
    case 'class':
      return 'Class';
    case 'method':
      return 'Method';
    case 'static-method':
      return 'Static';
    case 'type':
      return 'Type';
    case 'guide':
      return 'Guide';
    default:
      return kind;
  }
}

/** Get the previous and next entries in the flattened category order */
function getAdjacentEntries(currentSlug: string) {
  const ordered: string[] = [];
  for (const cat of SDK_CATEGORIES) {
    ordered.push(...cat.items);
  }
  const idx = ordered.indexOf(currentSlug);
  return {
    prev: idx > 0 ? getEntry(ordered[idx - 1]) : undefined,
    next: idx < ordered.length - 1 ? getEntry(ordered[idx + 1]) : undefined,
  };
}
